/**
 * TeamInsightsDashboard — manager "roster view" for AI performance insights.
 *
 * Lists the manager's direct reports with a color-coded deviation badge
 * (Aligned / Moderate Deviation / High Deviation) and the model's warning
 * flags, so a manager can quickly spot who needs a closer review. Clicking a
 * row opens the detailed `EmployeeInsightCard`.
 */

import React, { useEffect, useState } from "react";
import axios from "axios";
import {
  Alert,
  Box,
  Chip,
  CircularProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";

import PageTitle from "../../../components/Page Component/PageTitle";
import { managerTeamPredictionsUrl } from "../../../Service/Apis";
import { EmployeePredictionListItem } from "./Types/MLAnalyticsTypes";
import DeviationBadge from "./components/DeviationBadge";
import RemarksChips from "./components/RemarksChips";
import EmployeeInsightCard from "./EmployeeInsightCard";

const riskFlagColor: Record<string, "success" | "warning" | "error" | "default"> = {
  Low: "success",
  Medium: "warning",
  High: "error",
};

const formatRating = (value: number | null | undefined): string =>
  value === null || value === undefined ? "—" : value.toFixed(2);

interface TeamInsightsDashboardProps {
  /**
   * Set to true when rendering inside another page (e.g. a tab panel) that
   * already accounts for the fixed navbar's height, to avoid double spacing.
   */
  embedded?: boolean;
}

const TeamInsightsDashboard: React.FC<TeamInsightsDashboardProps> = ({ embedded = false }) => {
  const [rows, setRows] = useState<EmployeePredictionListItem[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedEmployeeId, setSelectedEmployeeId] = useState<string | null>(null);

  useEffect(() => {
    const fetchTeamPredictions = async () => {
      setLoading(true);
      setError(null);
      try {
        const token = localStorage.getItem("token");
        const response = await axios.get(managerTeamPredictionsUrl, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setRows(response.data || []);
      } catch (err) {
        setError("Failed to load team performance insights.");
      } finally {
        setLoading(false);
      }
    };

    fetchTeamPredictions();
  }, []);

  if (selectedEmployeeId) {
    return (
      <EmployeeInsightCard
        employeeId={selectedEmployeeId}
        onBack={() => setSelectedEmployeeId(null)}
        embedded={embedded}
      />
    );
  }

  return (
    <Box sx={{ mt: embedded ? 0 : 10, p: 3 }}>
      <PageTitle>Performance Insights</PageTitle>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        AI-assisted performance insights for your team, based on predicted vs. actual manager
        ratings and supporting HR/attendance/utilization data.
      </Typography>

      {loading && (
        <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
          <CircularProgress />
        </Box>
      )}

      {!loading && error && <Alert severity="error">{error}</Alert>}

      {!loading && !error && rows.length === 0 && (
        <Alert severity="info">No performance insight data is available for your team yet.</Alert>
      )}

      {!loading && !error && rows.length > 0 && (
        <TableContainer component={Paper} elevation={2}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Employee</TableCell>
                <TableCell>Role</TableCell>
                <TableCell>Predicted</TableCell>
                <TableCell>Actual</TableCell>
                <TableCell>Alignment</TableCell>
                <TableCell>Risk</TableCell>
                <TableCell>Warning Flags</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((row) => (
                <TableRow
                  key={row.employee_id}
                  hover
                  sx={{ cursor: "pointer" }}
                  onClick={() => setSelectedEmployeeId(row.employee_id)}
                >
                  <TableCell>
                    <Typography variant="body2" fontWeight={600}>
                      {row.employee_name || "—"}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {row.employee_code}
                    </Typography>
                  </TableCell>
                  <TableCell>{row.designation_name || "—"}</TableCell>
                  <TableCell>{formatRating(row.predicted_manager_rating)}</TableCell>
                  <TableCell>{formatRating(row.manager_overall_rating)}</TableCell>
                  <TableCell>
                    <DeviationBadge category={row.prediction_gap_category} />
                  </TableCell>
                  <TableCell>
                    <Chip
                      size="small"
                      label={row.risk_flag || "—"}
                      color={riskFlagColor[row.risk_flag] || "default"}
                      variant="outlined"
                    />
                  </TableCell>
                  <TableCell sx={{ maxWidth: 320 }}>
                    <RemarksChips remarks={row.remarks} />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  );
};

export default TeamInsightsDashboard;
